import { Helmet } from 'react-helmet-async'
import { GatsbySeo } from 'gatsby-plugin-next-seo'
import { getCMSPageDataByContentType } from 'src/cms/client'
import { slugify } from 'src/utils/slugfy'
import { SportsPageTemplate } from 'src/components/sports/SportsPageTemplate'
import { DepartmentPageTemplate } from 'src/components/department/DepartmentPageTemplate'
import CollectionPLPTemplate from 'src/components/cms/templates/CollectionPLPTemplate'
import CampanhaTemplate from 'src/components/cms/templates/CampanhaTemplate'
import type { CollectionPLPTemplateProps } from 'src/components/cms/templates/CollectionPLPTemplate'
import type { CampanhaTemplateProps } from 'src/components/cms/templates/CampanhaTemplate'
import type { PropsCMS } from 'src/components/sports/utils/types'
import type { SearchState } from '@faststore/sdk'
import type { SearchResult } from 'src/components/sections/ProductGalleryV2/types'
import getSportsPageServerSideData from 'src/utils/plp/getSportsPageServerSideData'
import { getShelfServerSideData } from 'src/utils/recommendations/getShelfServerSideData'
import { graphql } from 'gatsby'
import { getProductStripeData } from 'src/cms/hooks/getProductStripeData'
import { handleSSRError } from 'src/utils/handleSSRError'
import { resolveRenderTemplate } from 'src/utils/cms/resolveRenderTemplate'
import type { RenderTemplate } from 'src/utils/cms/resolveRenderTemplate'
import { getCollectionTemplateServerData } from 'src/utils/plp/getCollectionTemplateServerData'
import { getCampanhaTemplateServerData } from 'src/utils/getCampanhaTemplateServerData'

type ServerData = {
  serverData: {
    searchParams: SearchState
    searchResult: SearchResult
    renderTemplate?: RenderTemplate
  }
}

function SportsPage(props: PropsCMS & ServerData) {
  const {
    serverData,
    location: { pathname, search },
    data: { site },
  } = props

  if (serverData === null) {
    return null
  }

  const { cmsData, renderTemplate } = serverData

  if (renderTemplate === 'collection-plp') {
    return (
      <CollectionPLPTemplate
        serverData={
          serverData as unknown as CollectionPLPTemplateProps['serverData']
        }
        location={props.location}
        data={{ site }}
      />
    )
  }

  if (renderTemplate === 'campanha') {
    return (
      <CampanhaTemplate
        serverData={
          serverData as unknown as CampanhaTemplateProps['serverData']
        }
        location={props.location}
        data={{ site }}
      />
    )
  }

  const seo = cmsData.seo.siteMetadataWithSlug

  const canonicalUrl = `${site?.siteMetadata?.siteUrl}${pathname}${search}`

  return (
    <>
      <Helmet>
        <meta name="keywords" content={seo.keywords} />
        <meta property="og:image" content={seo.ogImage} />
      </Helmet>
      <GatsbySeo
        title={seo.title}
        description={seo.description}
        language="pt-br"
        canonical={canonicalUrl}
        openGraph={{
          images: [{ url: site?.siteMetadata?.ogImage ?? '' }],
          type: 'website',
          url: canonicalUrl,
          title: seo.title,
          description: seo.description,
        }}
      />

      {cmsData.type === 'department-page' && (
        <DepartmentPageTemplate {...props} />
      )}
      {cmsData.type === 'esportes' && (
        <SportsPageTemplate {...props} type="página de lista de produto" />
      )}
    </>
  )
}

/**
 * This query is run during SSG
 * */
export const querySSG = graphql`
  query SportsPage {
    site {
      ...SiteInformation
    }
  }
`

export const getServerData = async ({
  params: { slug },
  query,
}: {
  query: Record<string, string>
  params: Record<string, string>
}) => {
  let currentStep = 'initializing'

  try {
    currentStep = 'getProductStripeData'
    const stripeData = await getProductStripeData({
      CMS_VERSION_ID: query?.versionId ?? '',
    })

    const newSlug = slugify(slug, '+')

    let departmentCms = null

    currentStep = 'getCMSPageDataByContentType:department-page'
    try {
      departmentCms = await getCMSPageDataByContentType({
        contentType: 'department-page',
        params: {
          filters: { name: newSlug },
        },
        pageId: query?.pageId,
        versionId: query?.versionId,
      })
    } catch (error) {
      // In preview, pageId/versionId may belong to another content type.
      // Keep rendering using esportes data instead of failing the entire SSR.
      departmentCms = null
    }

    currentStep = 'getCMSPageDataByContentType:esportes'
    const sportsCms = await getCMSPageDataByContentType({
      contentType: 'esportes',
      pageId: query?.pageId ?? '',
      versionId: query?.versionId ?? '',
      params: {
        filters: { name: newSlug },
      },
    })

    if (!departmentCms && !sportsCms) {
      return {
        status: 301,
        props: null,
        headers: {
          location: `/404/?from=${slug}`,
        },
      }
    }

    const authoritativeCms = departmentCms ?? sportsCms
    const renderTemplate = resolveRenderTemplate(authoritativeCms)

    if (renderTemplate === 'collection-plp') {
      currentStep = 'getCollectionTemplateServerData'
      const result = await getCollectionTemplateServerData({
        slug,
        query,
        routePrefix: 'esportes',
        cmsData: authoritativeCms,
      })

      return { ...result, props: { ...result.props, renderTemplate } }
    }

    if (renderTemplate === 'campanha') {
      currentStep = 'getCampanhaTemplateServerData'
      const result = await getCampanhaTemplateServerData({
        slug,
        query,
        cmsData: {
          status: 200,
          props: {
            cmsData: authoritativeCms as NonNullable<typeof authoritativeCms>,
          },
          headers: { 'cache-control': 'no-cache' },
        },
      })

      return { ...result, props: { ...result.props, renderTemplate } }
    }

    if (departmentCms) {
      currentStep = 'getShelfServerSideData'
      const newSection = await getShelfServerSideData({
        sections: departmentCms.sections,
      })

      departmentCms.sections = newSection

      return {
        status: 200,
        props: {
          stripeData,
          cmsData: departmentCms,
          pageType: 'Esportes',
        },
        headers: {
          'cache-control': 'no-cache',
        },
      }
    }

    currentStep = 'getSportsPageServerSideData'
    const { searchParams, searchResult } = await getSportsPageServerSideData(
      sportsCms.sections,
      newSlug,
      query
    )

    return {
      status: 200,
      props: {
        stripeData,
        cmsData: sportsCms,
        searchParams,
        searchResult,
        pageType: 'Esportes',
      },
      headers: {
        'cache-control': 'no-cache',
      },
    }
  } catch (err) {
    handleSSRError({
      err: err as Error,
      query,
      slug: String(slug ?? '/esportes'),
      stepName: currentStep,
    })

    return {
      status: 500,
      props: {},
      headers: { 'cache-control': 'public, max-age=0, must-revalidate' },
    }
  }
}

export default SportsPage
